import React, { useState } from 'react';
import { motion } from 'motion/react';
import { Mail, Phone, MapPin, Send, CheckCircle } from 'lucide-react';

const Contact = () => {
  // Update page title
  React.useEffect(() => {
    document.title = "Contact Us - Kodamalas Krushi Foundation | Get in Touch";
  }, []);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const springConfig = {
    type: "spring",
    damping: 80,
    stiffness: 400,
    mass: 1
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    let response = null;
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      console.log('Submitting to:', `${backendUrl}/api/contact`);
      console.log('Form data:', formData);
      
      response = await fetch(`${backendUrl}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);

      if (response.ok) {
        const result = await response.json();
        console.log('Form submitted successfully:', result);
        setSubmitted(true);
        
        setTimeout(() => {
          setSubmitted(false);
          setFormData({
            name: '',
            email: '',
            phone: '',
            subject: '',
            message: ''
          });
        }, 3000);
      } else {
        const errorText = await response.text();
        console.error('Server error response:', errorText);
        throw new Error(`Server responded with status ${response.status}: ${errorText}`);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      if (response) {
        console.error('Response status:', response.status);
      }
      alert('There was an error submitting your message. Please try again.');
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: "Visit Us",
      details: ["SK Residency, Thumkunta", "Hyderabad, Telangana - 500078"]
    },
    {
      icon: Mail,
      title: "Email Us",
      details: ["kkfoundation2025@gmail.com"],
      link: "mailto:kkfoundation2025@gmail.com"
    },
    {
      icon: Phone,
      title: "Call Us",
      details: ["+91 8985709090", "+91 8985709595"],
      links: ["tel:+918985709090", "tel:+918985709595"]
    }
  ];

  return (
    <div className="min-h-screen pt-32 pb-20">
      {/* Hero Section */}
      <section className="px-6 md:px-8 lg:px-12 mb-20">
        <div className="container mx-auto max-w-4xl text-center">
          <motion.h1
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springConfig}
            className="display-hero text-[var(--text-primary)] mb-6"
          >
            Get in Touch
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springConfig, delay: 0.1 }}
            className="body-large text-[var(--text-secondary)]"
          >
            Have questions about our work or want to collaborate? We'd love to hear from you.
          </motion.p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="px-6 md:px-8 lg:px-12 mb-20">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {contactInfo.map((info, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ ...springConfig, delay: index * 0.1 }}
                className="bg-[var(--bg-light)] rounded-2xl p-8 text-center hover-lift"
              >
                <div className="w-14 h-14 bg-[var(--brand-primary)] rounded-xl flex items-center justify-center mx-auto mb-4">
                  <info.icon size={28} className="text-white" />
                </div>
                <h3 className="heading-medium text-[var(--text-primary)] mb-4">
                  {info.title}
                </h3>
                <div className="space-y-2">
                  {info.details.map((detail, idx) => (
                    info.link || info.links ? (
                      <a
                        key={idx}
                        href={info.link || info.links[idx]}
                        className="body-standard text-[var(--text-secondary)] hover:text-[var(--brand-primary)] transition-colors block"
                      >
                        {detail}
                      </a>
                    ) : (
                      <p key={idx} className="body-standard text-[var(--text-secondary)]">
                        {detail}
                      </p>
                    )
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact-form" className="px-6 md:px-8 lg:px-12">
        <div className="container mx-auto max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="glass-card rounded-3xl p-10 md:p-12 shadow-lg border border-[var(--border-light)]"
          >
            <div className="text-center mb-10">
              <h2 className="heading-large text-[var(--text-primary)] mb-4">
                Send Us a Message
              </h2>
              <p className="body-standard text-[var(--text-secondary)]">
                Fill out the form below and we'll get back to you as soon as possible
              </p>
            </div>

            {submitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-12"
              >
                <div className="w-20 h-20 bg-[var(--brand-primary)] rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle size={40} className="text-white" />
                </div>
                <h3 className="heading-medium text-[var(--text-primary)] mb-3">
                  Message Sent Successfully!
                </h3>
                <p className="body-standard text-[var(--text-secondary)]">
                  Thank you for reaching out. We'll respond to your inquiry shortly.
                </p>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="body-standard text-[var(--text-primary)] mb-2 block">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-6 py-4 border border-[var(--border-medium)] rounded-full body-standard text-[var(--text-primary)] focus:outline-none focus:border-[var(--brand-primary)] transition-colors"
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="body-standard text-[var(--text-primary)] mb-2 block">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-6 py-4 border border-[var(--border-medium)] rounded-full body-standard text-[var(--text-primary)] focus:outline-none focus:border-[var(--brand-primary)] transition-colors"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="phone" className="body-standard text-[var(--text-primary)] mb-2 block">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-6 py-4 border border-[var(--border-medium)] rounded-full body-standard text-[var(--text-primary)] focus:outline-none focus:border-[var(--brand-primary)] transition-colors"
                      placeholder="+91 1234567890"
                    />
                  </div>
                  <div>
                    <label htmlFor="subject" className="body-standard text-[var(--text-primary)] mb-2 block">
                      Subject *
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-6 py-4 border border-[var(--border-medium)] rounded-full body-standard text-[var(--text-primary)] focus:outline-none focus:border-[var(--brand-primary)] transition-colors"
                      placeholder="How can we help?"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="body-standard text-[var(--text-primary)] mb-2 block">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="6"
                    className="w-full px-6 py-4 border border-[var(--border-medium)] rounded-3xl body-standard text-[var(--text-primary)] focus:outline-none focus:border-[var(--brand-primary)] transition-colors resize-none"
                    placeholder="Tell us more about your inquiry..."
                  />
                </div>

                <div className="text-center">
                  <button
                    type="submit"
                    className="btn-primary flex items-center gap-2 mx-auto hover-scale"
                  >
                    Send Message
                    <Send size={18} />
                  </button>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      </section>

      {/* Get Involved Section */}
      <section className="px-6 md:px-8 lg:px-12 mt-20">
        <div className="container mx-auto max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="bg-[var(--bg-light)] rounded-3xl p-12 text-center"
          >
            <h2 className="heading-large text-[var(--text-primary)] mb-4">
              Join Our Mission
            </h2>
            <p className="body-large text-[var(--text-secondary)] mb-8 max-w-3xl mx-auto">
              Whether you're interested in volunteering, partnering with us, or supporting our cause, there are many ways to get involved with Kodamalas Krushi Foundation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="#contact-form" className="btn-primary">
                Become a Volunteer
              </a>
              <a href="#contact-form" className="btn-secondary">
                Partner With Us
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact;